import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { useInfinitePosts } from '../../shared/hooks/use-infinite-posts';
import { SocialPostsGrid } from './social-post-card/social-posts-grid';
import { SocialPostsGridHeader } from './social-posts-grid-header';

import { SORT_OPTION_LIST_WITHOUT_SCORE, type SortOption } from '@/entities/post/api/endpoint';
import {
  BoardsSelectionActionBar,
  requestSaveToBoardsDialog,
  useBoardsPosts,
} from '@/features/boards';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import { useSharedDateRange } from '@/shared/hooks/use-shared-date-range';
import { useRestoreScroll } from '../../insight-layout/hook/use-insight-scroll-container';
import { usePublicFilter } from '../../shared/public-filter/use-public-filter';

export function PostsPage() {
  return <PostsPageContent />;
}

function PostsPageContent() {
  const { query } = useSelectedSavedQuery();
  const { publicFilter } = usePublicFilter(query.id);
  const { dateRange } = useSharedDateRange();
  const [sortBy, setSortBy] = useState<SortOption>('view');
  const queryClient = useQueryClient();
  const { selectedPostIds, selectedCount, toggleSelectedPost, clearSelectedPosts } =
    useBoardsPosts();

  const isSample = query.type === 'public';

  const params = useMemo(
    () => ({
      queryId: query.id,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy,
      publicFilter,
    }),
    [query.id, dateRange, sortBy, publicFilter],
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useInfinitePosts(params);

  // Get all posts from all pages
  const allPosts = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.posts);
  }, [data]);

  // Get total count from API response (available from the first page)
  const totalCount = data?.pages?.[0]?.total;
  const savedBoardCountsByPostId = useMemo(
    () => Object.fromEntries(allPosts.map((post) => [post._id, post.board_count])),
    [allPosts],
  );
  useRestoreScroll();
  return (
    <div>
      <SocialPostsGridHeader
        type="posts"
        sortBy={sortBy}
        onSortChange={setSortBy}
        totalCount={totalCount}
        params={params}
        isSample={isSample}
        isLoading={isPending}
        sortOptionList={SORT_OPTION_LIST_WITHOUT_SCORE}
      />
      <SocialPostsGrid
        posts={allPosts}
        isLoading={isPending}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        onLoadMore={fetchNextPage}
        className="pb-24"
        selectedPostIds={selectedPostIds}
        onTogglePostSelect={toggleSelectedPost}
        savedBoardCountsByPostId={savedBoardCountsByPostId}
      />
      <BoardsSelectionActionBar
        selectedCount={selectedCount}
        onSaveSelected={async () => {
          await requestSaveToBoardsDialog({
            postIds: selectedPostIds,
            onSaved: async () => {
              clearSelectedPosts();
              await queryClient.invalidateQueries({ queryKey: ['posts'] });
            },
          });
        }}
        onClear={clearSelectedPosts}
      />
    </div>
  );
}
